import apiClient from '../config/api';
import { Facture, AcompteRequest, SoldeRequest, FactureRequestDTO } from '../types';

class FactureService {
  /**
   * Récupérer toutes les factures de l'utilisateur connecté
   */
  async getAll(): Promise<Facture[]> {
    const response = await apiClient.get<Facture[]>('/api/factures');
    return response.data;
  }

  /**
   * Récupérer une facture par son ID
   */
  async getById(id: number): Promise<Facture> {
    const response = await apiClient.get<Facture>(`/api/factures/${id}`);
    return response.data;
  }

  /**
   * Récupérer les factures d'un projet
   */
  async getByProjetId(projetId: number): Promise<Facture[]> {
    const response = await apiClient.get<Facture[]>(`/api/factures/projet/${projetId}`);
    return response.data;
  }

  /**
   * Créer une facture d'acompte pour un devis accepté
   * @param devisId - ID du devis accepté
   * @param pourcentageAcompte - Pourcentage de l'acompte (ex: 30 pour 30%)
   */
  async creerFactureAcompte(devisId: number, pourcentageAcompte: number): Promise<Facture> {
    const request: AcompteRequest = { devisId, pourcentageAcompte };
    const response = await apiClient.post<Facture>('/api/factures/acompte', request);
    return response.data;
  }

  /**
   * Créer une facture de solde pour un projet
   * Déduit automatiquement tous les acomptes déjà payés
   * @param projetId - ID du projet
   * @param devisId - ID du devis à solder
   */
  async creerFactureSolde(projetId: number, devisId: number): Promise<Facture> {
    const request: SoldeRequest = { projetId, devisId };
    const response = await apiClient.post<Facture>('/api/factures/solde', request);
    return response.data;
  }

  /**
   * Mettre à jour une facture (uniquement si elle n'est pas émise)
   * IMPORTANT: Lève une exception si la facture est émise (immuabilité légale)
   */
  async update(id: number, data: FactureRequestDTO): Promise<Facture> {
    const response = await apiClient.put<Facture>(`/api/factures/${id}`, data);
    return response.data;
  }

  /**
   * Marquer une facture comme payée
   */
  async markAsPaid(id: number): Promise<Facture> {
    const response = await apiClient.put<Facture>(`/api/factures/${id}/payer`);
    return response.data;
  }

  /**
   * Supprimer une facture (uniquement si elle n'est pas émise)
   * IMPORTANT: Lève une exception si la facture est émise (immuabilité légale)
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/api/factures/${id}`);
  }

  /**
   * Télécharger le PDF d'une facture généré par le backend
   * @param id - ID de la facture
   * @returns Le PDF en tant que Blob
   */
  async downloadPDF(id: number): Promise<Blob> {
    console.log('📄 Téléchargement du PDF de la facture ID:', id);
    try {
      const response = await apiClient.get(`/api/factures/${id}/pdf`, {
        responseType: 'blob',
      });
      console.log('✅ PDF téléchargé, taille:', response.data.size, 'bytes');
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors du téléchargement du PDF de la facture:', error);
      throw error;
    }
  }

  /**
   * Obtenir le PDF d'une facture en base64 généré par le backend
   * @param id - ID de la facture
   * @returns Le PDF encodé en base64
   */
  async getPDFBase64(id: number): Promise<string> {
    console.log('📄 Récupération du PDF en base64 de la facture ID:', id);
    try {
      const blob = await this.downloadPDF(id);

      // Convertir le Blob en base64
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          // Extraire la partie base64 (après "data:application/pdf;base64,")
          const base64Data = base64.split(',')[1];
          console.log('✅ PDF converti en base64, taille:', base64Data.length, 'caractères');
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error: any) {
      console.error('❌ Erreur lors de la conversion du PDF en base64:', error);
      throw error;
    }
  }
}

export default new FactureService();
